import React, { useEffect, useMemo, useState } from "react";
import { BACKEND_URL } from "../config";
import "./WorkoutSessionView.timer.css";

/**
 * WorkoutSessionView (cues + video + per-set tracking + timed exercise timer)
 * - Loads session
 * - Fetches workout library once to enrich exercises with focus/notes/youtube/raw
 * - Displays form cues + "Video" when available
 * - Tracks sets/reps/weight per set via performed_sets (partial save friendly)
 * - Adds a built-in timer UI for timed exercises
 */
export default function WorkoutSessionView({ token, sessionId, onBack, showToast }) {
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState(null);
  const [saving, setSaving] = useState(false);

  const [libLoading, setLibLoading] = useState(false);
  const [libraryById, setLibraryById] = useState({}); // exercise_id -> doc

  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [feedbackRating, setFeedbackRating] = useState("");
  const [feedbackNote, setFeedbackNote] = useState("");
  const [feedbackSaving, setFeedbackSaving] = useState(false);
  const [collapsedCompletedSets, setCollapsedCompletedSets] = useState({});
  const [timers, setTimers] = useState({}); // itemKey -> { initialSeconds, remainingSeconds, isRunning }

  const safeInt = (v) => {
    if (v === null || v === undefined || v === "") return null;
    const n = Number(v);
    return Number.isFinite(n) ? Math.trunc(n) : null;
  };

  const safeFloat = (v) => {
    if (v === null || v === undefined || v === "") return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };

  const getTimedSeconds = (it, lib = null) => {
    const candidates = [
      it?.duration_seconds,
      it?.planned_duration_seconds,
      it?.duration_sec,
      it?.planned_duration_sec,
      it?.duration,
      it?.planned_duration,
      it?.duration_s,
      it?.time_seconds,
      it?.seconds,
      lib?.duration_seconds,
      lib?.planned_duration_seconds,
      lib?.duration_sec,
      lib?.duration,
      lib?.seconds,
      lib?.raw?.duration_seconds,
      lib?.raw?.duration_sec,
      lib?.raw?.duration,
      lib?.raw?.seconds,
    ];

    for (const value of candidates) {
      const n = Number(value);
      if (Number.isFinite(n) && n > 0) return Math.max(1, Math.round(n));
    }

    const textCandidates = [
      it?.notes,
      it?.form_cues,
      lib?.notes,
      lib?.raw?.notes,
      lib?.raw?.description,
    ]
      .filter(Boolean)
      .map(String);

    for (const text of textCandidates) {
      const minMatch = text.match(/(\d+)\s*(min|mins|minute|minutes)\b/i);
      if (minMatch) return Number(minMatch[1]) * 60;

      const secMatch = text.match(/(\d+)\s*(sec|secs|second|seconds)\b/i);
      if (secMatch) return Number(secMatch[1]);
    }

    return null;
  };

  const formatClock = (totalSeconds) => {
    const safe = Math.max(0, Number(totalSeconds) || 0);
    const mins = Math.floor(safe / 60);
    const secs = safe % 60;
    return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
  };

  const primeTimer = (itemKey, seconds) => {
    if (!itemKey || !Number.isFinite(Number(seconds)) || Number(seconds) <= 0) return;
    const nextSeconds = Math.max(1, Math.round(Number(seconds)));
    setTimers((prev) => {
      const existing = prev[itemKey];
      if (existing && existing.initialSeconds === nextSeconds) return prev;
      return {
        ...prev,
        [itemKey]: {
          initialSeconds: nextSeconds,
          remainingSeconds: nextSeconds,
          isRunning: false,
        },
      };
    });
  };

  const startTimer = (itemKey, seconds) => {
    primeTimer(itemKey, seconds);
    setTimers((prev) => ({
      ...prev,
      [itemKey]: {
        initialSeconds: Math.max(1, Math.round(Number(seconds) || prev[itemKey]?.initialSeconds || 1)),
        remainingSeconds: prev[itemKey]?.remainingSeconds ?? Math.max(1, Math.round(Number(seconds) || 1)),
        isRunning: true,
      },
    }));
  };

  const pauseTimer = (itemKey) => {
    setTimers((prev) => ({
      ...prev,
      [itemKey]: {
        ...(prev[itemKey] || {}),
        isRunning: false,
      },
    }));
  };

  const resetTimer = (itemKey, seconds) => {
    const initial = Math.max(1, Math.round(Number(seconds) || timers[itemKey]?.initialSeconds || 1));
    setTimers((prev) => ({
      ...prev,
      [itemKey]: {
        initialSeconds: initial,
        remainingSeconds: initial,
        isRunning: false,
      },
    }));
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setTimers((prev) => {
        let changed = false;
        const nextState = {};
        for (const [key, timer] of Object.entries(prev)) {
          if (!timer?.isRunning) {
            nextState[key] = timer;
            continue;
          }
          const nextRemaining = Math.max(0, Number(timer.remainingSeconds || 0) - 1);
          if (nextRemaining !== timer.remainingSeconds) changed = true;
          nextState[key] = {
            ...timer,
            remainingSeconds: nextRemaining,
            isRunning: nextRemaining > 0,
          };
        }
        return changed ? nextState : prev;
      })
    }, 1000)

    return () => clearInterval(interval)
  }, [])

  const ensurePerformedSets = (it) => {
    const ps = Array.isArray(it?.performed_sets) ? it.performed_sets : [];
    if (ps.length) return ps;

    const plannedSets = safeInt(it?.planned_sets ?? it?.sets) ?? 0;
    const plannedReps = safeInt(it?.planned_reps ?? it?.reps);
    const weightHint = safeFloat(it?.planned_weight ?? it?.weight ?? it?.recommended_weight);

    if (!plannedSets || plannedSets <= 0) return [];
    return Array.from({ length: plannedSets }, (_, i) => ({
      set_number: i + 1,
      reps: plannedReps,
      weight: weightHint,
      rpe: null,
      completed: false,
      notes: null,
    }));
  };

  const loadSession = async () => {
    if (!token || !sessionId) return;
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/workouts/sessions/${encodeURIComponent(sessionId)}`, {
        headers: authHeaders,
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      const s = data.session || data;

      const items = Array.isArray(s?.items_performed) ? s.items_performed : [];
      const nextItems = items.map((it) => ({
        ...it,
        performed_sets: ensurePerformedSets(it),
      }));
      setSession({ ...s, items_performed: nextItems });
      setFeedbackRating(s?.feedback_rating || "");
      setFeedbackNote(s?.feedback_note || "");
    } catch (e) {
      showToast?.("Failed to load workout: " + (e?.message || String(e)), "error");
      setSession(null);
    } finally {
      setLoading(false);
    }
  };

  const saveFeedback = async (ratingOverride = null) => {
    if (!token || !sessionId) return;
    const rating = ratingOverride || feedbackRating;
    if (!rating) return;

    setFeedbackSaving(true);
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/workouts/sessions/${encodeURIComponent(sessionId)}/feedback`,
        {
          method: "POST",
          headers: { ...authHeaders, "Content-Type": "application/json" },
          body: JSON.stringify({
            feedback_rating: rating,
            feedback_note: feedbackNote || "",
          }),
        }
      );

      if (!res.ok) throw new Error(await res.text());
      setFeedbackRating(rating);
    } catch (e) {
      showToast?.("Feedback save failed: " + (e?.message || String(e)), "error");
    } finally {
      setFeedbackSaving(false);
    }
  };

  const loadWorkoutLibrary = async () => {
    if (!token) return;
    setLibLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/workouts/library`, { headers: authHeaders });
      if (!res.ok) return;
      const data = await res.json();
      const items = Array.isArray(data.items) ? data.items : [];
      const map = {};
      for (const it of items) {
        if (it?.exercise_id) map[it.exercise_id] = it;
      }
      setLibraryById(map);
    } catch (e) {
      console.warn("Workout library load failed:", e?.message || e);
    } finally {
      setLibLoading(false);
    }
  };

  useEffect(() => {
    loadSession();
    loadWorkoutLibrary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, token]);

  useEffect(() => {
    const items = Array.isArray(session?.items_performed) ? session.items_performed : [];
    items.forEach((it, idx) => {
      const lib = it?.exercise_id ? libraryById[it.exercise_id] : null;
      const seconds = getTimedSeconds(it, lib);
      if (seconds) {
        const itemKey = String(it.item_id || idx);
        primeTimer(itemKey, seconds);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, libraryById]);

  const updateItem = (idx, patch) => {
    setSession((prev) => {
      const items = Array.isArray(prev?.items_performed) ? prev.items_performed.slice() : [];
      items[idx] = { ...(items[idx] || {}), ...patch };
      return { ...prev, items_performed: items };
    });
  };

  const updateSet = (itemIdx, setIdx, patch) => {
    setSession((prev) => {
      const items = Array.isArray(prev?.items_performed) ? prev.items_performed.slice() : [];
      const it = items[itemIdx] || {};
      const ps = ensurePerformedSets(it).slice();
      ps[setIdx] = { ...(ps[setIdx] || {}), ...patch, set_number: (ps[setIdx]?.set_number ?? setIdx + 1) };
      items[itemIdx] = { ...it, performed_sets: ps };
      return { ...prev, items_performed: items };
    });
  };

  const toggleCompletedCollapse = (itemIdx, setIdx, completed) => {
    const key = `${itemIdx}-${setIdx}`;
    setCollapsedCompletedSets((prev) => ({
      ...prev,
      [key]: !!completed,
    }));
  };

  const setPerformedSetCount = (itemIdx, nextCount) => {
    setSession((prev) => {
      const items = Array.isArray(prev?.items_performed) ? prev.items_performed.slice() : [];
      const it = items[itemIdx] || {};
      const current = ensurePerformedSets(it).slice();

      const n = Math.max(0, Math.min(99, Number(nextCount) || 0));
      const plannedReps = safeInt(it?.planned_reps ?? it?.reps);
      const weightHint = safeFloat(it?.planned_weight ?? it?.weight ?? it?.recommended_weight);

      let out = current;
      if (n < current.length) {
        out = current.slice(0, n);
      } else if (n > current.length) {
        out = current.slice();
        for (let i = current.length; i < n; i++) {
          out.push({
            set_number: i + 1,
            reps: plannedReps,
            weight: weightHint,
            rpe: null,
            completed: false,
            notes: null,
          });
        }
      }

      out = out.map((row, i) => ({ ...row, set_number: i + 1 }));
      items[itemIdx] = { ...it, performed_sets: out };
      return { ...prev, items_performed: items };
    });
  };

  const save = async () => {
    if (!token || !sessionId || !session) return;
    setSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/workouts/sessions/${encodeURIComponent(sessionId)}`, {
        method: "PUT",
        headers: { ...authHeaders, "Content-Type": "application/json" },
        body: JSON.stringify({
          items_performed: (session.items_performed || []).map((it) => ({
            ...it,
            performed_sets: Array.isArray(it?.performed_sets) ? it.performed_sets : [],
          })),
        }),
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json().catch(() => null);
      if (data?.session) setSession(data.session);
    } catch (e) {
      showToast?.("Save failed: " + (e?.message || String(e)), "error");
    } finally {
      setSaving(false);
    }
  };

  const finish = async () => {
    if (!token || !sessionId) return;
    setSaving(true);
    try {
      await save();

      const res = await fetch(`${BACKEND_URL}/api/workouts/sessions/${encodeURIComponent(sessionId)}/finish`, {
        method: "POST",
        headers: authHeaders,
      });
      if (!res.ok) throw new Error(await res.text());
      showToast?.("✅ Workout complete!");
      onBack?.();
    } catch (e) {
      showToast?.("Finish failed: " + (e?.message || String(e)), "error");
    } finally {
      setSaving(false);
    }
  };

  const title = session?.title || session?.event_title || "Workout";

  if (loading) {
    return (
      <div className="container" style={{ maxWidth: 900 }}>
        <div className="heading-2">Workout</div>
        <div className="body-medium" style={{ color: "var(--text-secondary)" }}>Loading…</div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="container" style={{ maxWidth: 900 }}>
        <button className="btn-secondary" onClick={onBack}>Back</button>
        <div className="body-medium" style={{ marginTop: 12 }}>No workout session found.</div>
      </div>
    );
  }

  return (
    <div className="container" style={{ maxWidth: 900 }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
        <button className="btn-secondary" type="button" onClick={onBack}>← Back</button>
        <div className="heading-3" style={{ margin: 0 }}>{title}</div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button className="btn-secondary" type="button" onClick={save} disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </button>
          <button className="btn-primary" type="button" onClick={finish} disabled={saving}>
            Finish
          </button>
        </div>
      </div>

      <div className="card" style={{ marginTop: 16, padding: 16 }}>
        <div className="body-small" style={{ color: "var(--text-muted)" }}>
          Started: {String(session.started_at || "").slice(0, 19).replace("T", " ")}
          {libLoading ? " • Loading cues…" : ""}
        </div>
      </div>

      <div style={{ marginTop: 16, display: "grid", gap: 12 }}>
        {(session.items_performed || []).map((it, idx) => {
          const lib = it.exercise_id ? libraryById[it.exercise_id] : null;

          const cues = (lib?.notes || it.notes || it.form_cues || "").trim();
          const focus = (lib?.focus || "").trim();
          const section = (lib?.section || lib?.top_group || "").trim();
          const video = (lib?.youtube_url || it.video_url || "").trim();

          const performedSets = ensurePerformedSets(it);

          const plannedSets = it.planned_sets ?? it.sets;
          const plannedReps = it.planned_reps ?? it.reps;
          const plannedWeight = it.planned_weight ?? it.weight;
          const timedSeconds = getTimedSeconds(it, lib);
          const itemKey = String(it.item_id || idx);
          const timer = timers[itemKey];

          return (
            <div key={it.item_id || idx} className="card" style={{ padding: 16 }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 12,
                  alignItems: "flex-start",
                  flexWrap: "wrap",
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 900, fontSize: 16 }}>{it.name}</div>
                  <div className="body-small" style={{ color: "var(--text-secondary)", marginTop: 6 }}>
                    Planned: {(plannedSets ?? "—")} sets × {(plannedReps ?? "—")} reps
                    {plannedWeight ? ` @ ${plannedWeight}` : ""}
                    {timedSeconds ? ` • ${Math.round(timedSeconds / 60) >= 1 && timedSeconds % 60 === 0 ? `${Math.round(timedSeconds / 60)} min` : `${timedSeconds} sec`}` : ""}
                    {focus ? ` • ${focus}` : ""}
                    {section ? ` • ${section}` : ""}
                  </div>
                </div>

                {video ? (
                  <a
                    href={video}
                    target="_blank"
                    rel="noreferrer"
                    className="btn-secondary"
                    style={{ padding: "10px 14px", textDecoration: "none" }}
                    title="Watch form video"
                  >
                    ▶ Video
                  </a>
                ) : null}
              </div>

              {timedSeconds ? (
                <div className="wsv-timer-card">
                  <div className="wsv-timer-head">
                    <div>
                      <div className="body-small wsv-timer-label">Timed exercise</div>
                      <div className="wsv-timer-clock">{formatClock(timer?.remainingSeconds ?? timedSeconds)}</div>
                    </div>
                    <div className="body-small" style={{ color: "var(--text-muted)" }}>
                      Target: {formatClock(timer?.initialSeconds ?? timedSeconds)}
                    </div>
                  </div>

                  <div className="wsv-timer-bar">
                    <div
                      className="wsv-timer-bar-fill"
                      style={{
                        width: `${Math.max(
                          0,
                          Math.min(
                            100,
                            (((timer?.initialSeconds ?? timedSeconds) - (timer?.remainingSeconds ?? timedSeconds)) /
                              Math.max(1, (timer?.initialSeconds ?? timedSeconds))) *
                              100
                          )
                        )}%`,
                      }}
                    />
                  </div>

                  <div className="wsv-timer-actions">
                    {!timer?.isRunning ? (
                      <button
                        type="button"
                        className="btn-primary"
                        onClick={() => startTimer(itemKey, timedSeconds)}
                      >
                        {Number(timer?.remainingSeconds ?? timedSeconds) <= 0 ? "Restart" : "Start Timer"}
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="btn-secondary"
                        onClick={() => pauseTimer(itemKey)}
                      >
                        Pause
                      </button>
                    )}

                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={() => resetTimer(itemKey, timedSeconds)}
                    >
                      Reset
                    </button>
                  </div>
                </div>
              ) : null}

              {cues ? (
                <div
                  style={{
                    marginTop: 12,
                    padding: 12,
                    borderRadius: 12,
                    border: "1px solid var(--border-light)",
                    background: "var(--bg-section)",
                  }}
                >
                  <div
                    className="body-small"
                    style={{ color: "var(--text-muted)", fontWeight: 800, marginBottom: 6 }}
                  >
                    Form cues
                  </div>
                  <div
                    className="body-small"
                    style={{ color: "var(--text-primary)", whiteSpace: "pre-wrap" }}
                  >
                    {cues}
                  </div>
                </div>
              ) : null}

              <div style={{ marginTop: 14 }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 12,
                    alignItems: "center",
                    flexWrap: "wrap",
                  }}
                >
                  <div className="body-small" style={{ color: "var(--text-muted)", fontWeight: 900 }}>
                    Sets
                  </div>

                  <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                    <label className="body-small" style={{ color: "var(--text-secondary)" }}>
                      Set count
                    </label>
                    <input
                      className="form-input"
                      style={{ width: 110 }}
                      type="number"
                      min={0}
                      max={99}
                      value={performedSets.length}
                      onChange={(e) => setPerformedSetCount(idx, e.target.value)}
                    />
                  </div>
                </div>

                {performedSets.length ? (
                  <div style={{ marginTop: 10, display: "grid", gap: 10 }}>
                    {performedSets.map((row, sIdx) => {
                      const setKey = `${idx}-${sIdx}`;
                      const isCollapsed = !!collapsedCompletedSets[setKey] && !!row.completed;

                      return (
                        <div
                          key={`${it.item_id || idx}-set-${sIdx}`}
                          style={{
                            border: "1px solid var(--border-light)",
                            borderRadius: 12,
                            padding: 12,
                            background: "var(--bg-card)",
                            display: "grid",
                            gap: 10,
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              gap: 12,
                              flexWrap: "wrap",
                            }}
                          >
                            <div style={{ fontWeight: 900 }}>
                              Set {sIdx + 1}
                              {row.completed ? (
                                <span
                                  className="body-small"
                                  style={{
                                    marginLeft: 8,
                                    color: "var(--text-secondary)",
                                    fontWeight: 700,
                                  }}
                                >
                                  • Completed
                                </span>
                              ) : null}
                            </div>

                            <div
                              style={{
                                display: "flex",
                                gap: 10,
                                alignItems: "center",
                                flexWrap: "wrap",
                              }}
                            >
                              {row.completed ? (
                                <button
                                  type="button"
                                  className="btn-secondary"
                                  onClick={() =>
                                    setCollapsedCompletedSets((prev) => ({
                                      ...prev,
                                      [setKey]: !prev[setKey],
                                    }))
                                  }
                                  aria-label={isCollapsed ? "Expand completed set" : "Collapse completed set"}
                                  title={isCollapsed ? "Expand" : "Collapse"}
                                  style={{
                                    width: 36,
                                    height: 36,
                                    borderRadius: "50%",
                                    padding: 0,
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontSize: 20,
                                    lineHeight: 1,
                                    fontWeight: 800,
                                  }}
                                >
                                  {isCollapsed ? "+" : "-"}
                                </button>
                              ) : null}

                              <label
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 8,
                                  cursor: "pointer",
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={!!row.completed}
                                  onChange={(e) => {
                                    const checked = !!e.target.checked;
                                    updateSet(idx, sIdx, { completed: checked });
                                    toggleCompletedCollapse(idx, sIdx, checked);
                                  }}
                                />
                                <span
                                  className="body-small"
                                  style={{ color: "var(--text-secondary)", fontWeight: 800 }}
                                >
                                  Completed
                                </span>
                              </label>
                            </div>
                          </div>

                          {isCollapsed ? (
                            <div className="body-small" style={{ color: "var(--text-muted)" }}>
                              Reps: {row.reps ?? "—"} • Weight: {row.weight ?? "—"} • RPE: {row.rpe ?? "—"}
                              {row.notes ? ` • Notes: ${row.notes}` : ""}
                            </div>
                          ) : (
                            <>
                              <div
                                style={{
                                  display: "grid",
                                  gridTemplateColumns: "1fr 1fr 1fr",
                                  gap: 12,
                                }}
                              >
                                <div className="form-group" style={{ marginBottom: 0 }}>
                                  <label className="body-small" style={{ color: "var(--text-secondary)" }}>
                                    Reps
                                  </label>
                                  <input
                                    className="form-input"
                                    type="number"
                                    value={row.reps ?? ""}
                                    onChange={(e) =>
                                      updateSet(idx, sIdx, {
                                        reps: e.target.value === "" ? null : Number(e.target.value),
                                      })
                                    }
                                  />
                                </div>

                                <div className="form-group" style={{ marginBottom: 0 }}>
                                  <label className="body-small" style={{ color: "var(--text-secondary)" }}>
                                    Weight
                                  </label>
                                  <input
                                    className="form-input"
                                    type="number"
                                    value={row.weight ?? ""}
                                    onChange={(e) =>
                                      updateSet(idx, sIdx, {
                                        weight: e.target.value === "" ? null : Number(e.target.value),
                                      })
                                    }
                                  />
                                </div>

                                <div className="form-group" style={{ marginBottom: 0 }}>
                                  <label className="body-small" style={{ color: "var(--text-secondary)" }}>
                                    RPE (1–10)
                                  </label>
                                  <input
                                    className="form-input"
                                    type="number"
                                    min={1}
                                    max={10}
                                    value={row.rpe ?? ""}
                                    onChange={(e) =>
                                      updateSet(idx, sIdx, {
                                        rpe: e.target.value === "" ? null : Number(e.target.value),
                                      })
                                    }
                                  />
                                </div>
                              </div>

                              <div className="form-group" style={{ marginBottom: 0 }}>
                                <label className="body-small" style={{ color: "var(--text-secondary)" }}>
                                  Notes (optional)
                                </label>
                                <input
                                  className="form-input"
                                  type="text"
                                  value={row.notes ?? ""}
                                  onChange={(e) =>
                                    updateSet(idx, sIdx, {
                                      notes: e.target.value === "" ? null : e.target.value,
                                    })
                                  }
                                />
                              </div>
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="body-small" style={{ marginTop: 10, color: "var(--text-muted)" }}>
                    No sets yet — increase set count to start tracking.
                  </div>
                )}
              </div>

              {lib?.raw ? (
                <details style={{ marginTop: 12 }}>
                  <summary
                    className="body-small"
                    style={{ cursor: "pointer", color: "var(--brand-primary)", fontWeight: 800 }}
                  >
                    View full exercise details
                  </summary>
                  <div style={{ marginTop: 10, display: "grid", gap: 10 }}>
                    {Object.entries(lib.raw).map(([k, v]) => (
                      <div
                        key={k}
                        style={{
                          border: "1px solid var(--border-light)",
                          borderRadius: 10,
                          padding: 10,
                          background: "var(--bg-card)",
                        }}
                      >
                        <div className="body-small" style={{ color: "var(--text-muted)", fontWeight: 800 }}>
                          {k}
                        </div>
                        <div
                          className="body-small"
                          style={{ color: "var(--text-secondary)", whiteSpace: "pre-wrap" }}
                        >
                          {String(v ?? "")}
                        </div>
                      </div>
                    ))}
                  </div>
                </details>
              ) : null}
            </div>
          );
        })}
      </div>

      <div className="card" style={{ marginTop: 16, padding: 16 }}>
        <div style={{ fontWeight: 900, marginBottom: 8 }}>Workout Feedback</div>
        <div className="body-small" style={{ color: "var(--text-secondary)", marginBottom: 12 }}>
          This will be sent to AI when generating future workouts.
        </div>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
          <button
            type="button"
            className={feedbackRating === "up" ? "btn-primary" : "btn-secondary"}
            onClick={() => {
              setFeedbackRating("up");
              saveFeedback("up");
            }}
            disabled={feedbackSaving}
          >
            👍 Thumbs Up
          </button>

          <button
            type="button"
            className={feedbackRating === "down" ? "btn-primary" : "btn-secondary"}
            onClick={() => {
              setFeedbackRating("down");
              saveFeedback("down");
            }}
            disabled={feedbackSaving}
          >
            👎 Thumbs Down
          </button>
        </div>

        <div className="form-group" style={{ marginBottom: 0 }}>
          <label className="body-small" style={{ color: "var(--text-secondary)" }}>
            Optional note
          </label>
          <textarea
            className="form-input"
            rows={2}
            value={feedbackNote}
            onChange={(e) => setFeedbackNote(e.target.value)}
            placeholder="Too easy, loved the flow, shoulder didn’t like this, etc."
          />
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 10 }}>
          <button
            type="button"
            className="btn-secondary"
            onClick={() => saveFeedback()}
            disabled={feedbackSaving || !feedbackRating}
          >
            {feedbackSaving ? "Saving…" : "Save Feedback"}
          </button>
        </div>
      </div>
    </div>
  );
}
